import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Field, Modal, TextArea, TextInput } from '@bcecloud/portal-ui';
import { logCall, type DealSummary, type RepWriteApplied } from '@/lib/sales-workspace';
import { WriteError } from './write-error';

/** Salesforce Log a Call's default subject. */
const DEFAULT_SUBJECT = 'Call';

export interface LogCallDialogProps {
  /** The deal the call is about; null closes the dialog. */
  readonly deal: DealSummary | null;
  readonly onClose: () => void;
  readonly onDone: (result: RepWriteApplied) => void;
}

/** "Log a call": a completed call on the deal, dated today, saved as the rep. */
export function LogCallDialog({ deal, onClose, onDone }: LogCallDialogProps): React.ReactElement | null {
  if (!deal) return null;
  return <LogCallForm key={deal.id} deal={deal} onClose={onClose} onDone={onDone} />;
}

function LogCallForm({ deal, onClose, onDone }: LogCallDialogProps & { readonly deal: DealSummary }): React.ReactElement {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [notes, setNotes] = useState('');
  const save = useMutation({
    mutationFn: () => logCall(deal.id, { subject: subject.trim(), ...(notes.trim() ? { notes: notes.trim() } : {}) }),
    onSuccess: onDone,
  });

  return (
    <Modal
      open
      title="Log a call"
      subtitle={[deal.name, deal.accountName].filter(Boolean).join(' · ')}
      onClose={() => {
        if (!save.isPending) onClose();
      }}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={subject.trim() === '' || save.isPending}>
            {save.isPending ? 'Saving…' : 'Log call'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field label="Subject" htmlFor="call-subject" required>
          <TextInput id="call-subject" value={subject} maxLength={255} onChange={(event) => setSubject(event.target.value)} />
        </Field>
        <Field label="Notes" htmlFor="call-notes" hint="Saved on the call in Salesforce.">
          <TextArea id="call-notes" rows={4} value={notes} maxLength={4000} onChange={(event) => setNotes(event.target.value)} />
        </Field>
        {save.isError ? <WriteError error={save.error} /> : null}
      </div>
    </Modal>
  );
}
